import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Rectangle, Circle } from '@react-google-maps/api';
import { styled } from 'styled-components';
import { useParams } from 'react-router-dom';
import { useHasProspector } from '/imports/hooks';
import { toast } from 'react-toastify';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { modelClass, ProspectBatch, BatchRectangle, BatchCircle, BatchMeasurement } from '/imports/api/prospectBatches';
import { addProspectToBatch, cancelProspectEnrichments, enqueueIncompleteEnrichments, enqueueProspectEnrichments, enrichProspect, exportProspectsToXlsx, getProspectEnrichProgress, getProspectFullDetails, Prospect } from '/imports/api/prospects';
import { Page } from '/imports/ui/Page';
import { sanDiegoCoords, useGoogleMap } from '/imports/gmaps';
import { GmapsAddress } from '/imports/gmaps';
import { Loading } from '/imports/ui/Loading';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { ProspectorMap, MapLayer, useProspectorLayers, DrawingTool, DrawingMode, DrawnShape, MeasurementTool } from '/imports/ui/ProspectorMap';
import { Polygon } from 'geojson';
import { listFootprintsInPolygon, geocodeFootprint, BuildingFootprint, FootprintEnrichment } from '/imports/api/buildingFootprints';
import { FootprintFiltersModal, FootprintFilters } from '/imports/ui/FootprintFiltersModal';
import { FootprintDetailModal, FootprintModalState } from '/imports/ui/FootprintDetailModal';
import { AddressInput } from '/imports/ui/AddressInput';
import { Checkbox } from '/imports/ui/Checkbox';
import { FileInput } from '/imports/ui/FileInput';
import { FormField } from '/imports/ui/FormField';
import { ListMethodCatalog } from '/imports/ui/ListMethodCatalog';
import { Button } from '/imports/ui/Button';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { Icon } from '/imports/ui/Icon';
import { toastOnError } from '/imports/ui/utils';
import { strings } from '/imports/strings';
import { clientDownloadUrl } from '/imports/utils';
import { css } from '/imports/ui/css';
import { modelClass as prospectModelClass } from '/imports/api/prospects';
import { maxTextImportBytes } from '/imports/api/files';

const savedRegionOptions = {
  strokeColor: '#f57c00', strokeOpacity: 0.9, strokeWeight: 3,
  fillColor: '#f57c00', fillOpacity: 0.08, clickable: false,
};

/**
 * Returns a prospect's map point: parcel centroid for APNs, otherwise the address location.
 * @param p - prospect
 * @returns lat/lng, or undefined if unavailable
 */
const prospectLatLng = (p: Prospect): { lat: number, lng: number } | undefined =>
  p.type === 'apn' ? p.parcel?.centroid : p.address?.location;

/**
 * Ray-casting point-in-polygon test for a single ring.
 * @param lng - point longitude
 * @param lat - point latitude
 * @param ring - polygon ring as [lng, lat] pairs
 * @returns true if the point is inside the ring
 */
const pointInRing = (lng: number, lat: number, ring: number[][]) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
};

const ProspectBatchPageStyled = styled(Page)`
  .map-wrap {
    position: relative;
    margin-top: 1rem;
  }
  .upload-file {
    display: flex;
    flex-direction: column;
    .target {
      height: calc(${css.inputHeight} - 4px);
      padding-top: 0;
      padding-bottom: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      .icon {
        display: none;
      }
      div:last-child {
        flex: 1;
        align-content: center;
      }
    }
  }
  .footer-wrap {
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
    .prospects-panel {
      width: 50%;
      h2 {
        margin-top: 0;
        .cancel-enrich {
          font-size: 0.85rem;
          color: ${css.red2};
          cursor: pointer;
          margin-left: 0.5rem;
          text-decoration: underline;
        }
        .enrich-incomplete {
          font-size: 0.85rem;
          cursor: pointer;
          margin-left: 0.5rem;
          text-decoration: underline;
        }
      }
    }
    .map-wrap {
      width: 50%;
      height: 40rem;
      .prospector-map-wrap {
        display: flex;
        height: 100%;
        position: relative;
        .map-loading {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(255, 255, 255, 0.9);
          padding: 0.75rem 1.25rem;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          z-index: 10;
        }
        .footprint-list {
          height: 100%;
          width: 16rem;
          overflow: auto;
          display: flex;
          flex-direction: column;
          .footprint-list-header {
            display: flex;
            justify-content: center;
            gap: 0.5rem;
            padding: 0.5rem;
            border-bottom: 1px solid ${css.grey4};
            position: sticky;
            top: 0;
            background: ${css.white};
            z-index: 1;
          }
          .footprint-row {
            padding: 0.35rem 1rem;
            border-bottom: 1px solid ${css.grey5};
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 0.5rem;
            &:hover {
              background: ${css.grey7};
            }
            &.in-batch {
              background: ${css.grey6};
            }
            &.loading {
              opacity: 0.6;
            }
          }
        }
      }
    }
  }
  .prospects-panel {
    max-height: 40rem;
  }
  .prospect-catalog-buttons {
    display: inline-flex;
    gap: 1rem;
  }
  .file-progress {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  tr.enriching td {
    background: #c2fffa;
  }
  .drawing-tools {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin-bottom: 0.5rem;
    button {
      padding: 0.35rem 0.75rem;
      font-size: 0.85rem;
      &.active {
        background: ${css.primaryColor};
        color: ${css.white};
      }
    }
    .drawing-hint {
      font-size: 0.8rem;
      color: ${css.grey3};
      margin-left: 0.5rem;
    }
  }
  .enrichment-flag {
    margin-top: auto;
    label {
      display: flex;
      align-items: center;
    }
  }
`;

/** Prospect-batch editor: address/file/footprint intake, map drawing, and enrichment. */
export const ProspectBatchPage = () => {
  const { _id } = useParams<any>();
  const canProspect = useHasProspector();
  const { isLoaded } = useGoogleMap();
  const defaultUpdate: Partial<ProspectBatch> = { _id, active: true };
  const [update, setUpdate] = useState<Partial<ProspectBatch>>(defaultUpdate);
  const documentRef = useRef<ProspectBatch>(undefined);
  const [addressKey, setAddressKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const prospectQuery = useMemo(() => ({ prospectBatchId: _id }), [_id]);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral | undefined>();
  const [mapZoom, setMapZoom] = useState<number | undefined>();
  const [mapInstance, setMapInstance] = useState<google.maps.Map>();
  const { layers, toggles, loading: layersLoading } = useProspectorLayers(mapInstance);
  const [drawingMode, setDrawingMode] = useState<DrawingMode>('rectangle');
  const [rectangles, setRectangles] = useState<BatchRectangle[]>([]);
  const [circles, setCircles] = useState<BatchCircle[]>([]);
  const [measurements, setMeasurements] = useState<BatchMeasurement[]>([]);
  useEffect(() => {
    if (!_id) return;
    (async () => {
      const batch = await modelClass.getMethod.call(_id);
      setRectangles(batch?.rectangles || []);
      setCircles(batch?.circles || []);
      setMeasurements(batch?.measurements || []);
    })();
  }, [_id]);
  const handleMeasurementsChange = (next: BatchMeasurement[]) => {
    setMeasurements(next);
    if (_id) modelClass.updateMethod.call({ _id, measurements: next });
  };
  // Ref guards so saved regions are framed only once, not re-fit after the user pans or draws.
  const fittedRegionsRef = useRef(false);
  useEffect(() => {
    if (fittedRegionsRef.current || !mapInstance) return;
    if (!rectangles.length && !circles.length) return;
    const bounds = new google.maps.LatLngBounds();
    for (const r of rectangles) {
      bounds.extend({ lat: r.north, lng: r.east });
      bounds.extend({ lat: r.south, lng: r.west });
    }
    for (const c of circles) {
      const latDelta = c.radius / 111320; // meters per degree latitude
      const lngDelta = c.radius / ((111320 * Math.cos((c.center.lat * Math.PI) / 180)) || 1);
      bounds.extend({ lat: c.center.lat + latDelta, lng: c.center.lng + lngDelta });
      bounds.extend({ lat: c.center.lat - latDelta, lng: c.center.lng - lngDelta });
    }
    if (!bounds.isEmpty()) {
      mapInstance.fitBounds(bounds, 64);
      fittedRegionsRef.current = true;
    }
  }, [mapInstance, rectangles, circles]);
  const [footprints, setFootprints] = useState<BuildingFootprint[]>([]);
  const [filters, setFilters] = useState<FootprintFilters>({
    'minRoofArea': 20000,
    'maxRoofArea': Number.MAX_SAFE_INTEGER,
    'limitResults': 100,
    'includeResi': false,
  });
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const filteredFootprints = useMemo(() => {
    const { minRoofArea, maxRoofArea, limitResults, includeResi } = filters;
    return footprints
      .filter((f) => (f.area ?? 0) >= minRoofArea && (f.area ?? 0) <= maxRoofArea)
      .filter((f) => includeResi || (f.properties as any)?.subtype !== 'residential')
      .slice(0, limitResults);
  }, [footprints, filters]);
  const batchedFootprintIds = useMemo(() => {
    const points = prospects.map(prospectLatLng).filter(Boolean) as { lat: number, lng: number }[];
    const ids = new Set<string | number>();
    if (!points.length) return ids;
    for (const f of filteredFootprints) {
      const ring = f.geometry?.coordinates?.[0];
      if (!ring) continue;
      if (points.some((pt) => pointInRing(pt.lng, pt.lat, ring))) ids.add(f.id ?? f._id);
    }
    return ids;
  }, [filteredFootprints, prospects]);
  const [fetchingFootprints, setFetchingFootprints] = useState(false);
  const handleDrawingComplete = async (polygon: Polygon, shape: DrawnShape) => {
    if (shape.type === 'rectangle') {
      const next = [...rectangles, shape.bounds];
      setRectangles(next);
      if (_id) modelClass.updateMethod.call({ _id, rectangles: next });
    }
    else {
      const next = [...circles, { center: shape.center, radius: shape.radius }];
      setCircles(next);
      if (_id) modelClass.updateMethod.call({ _id, circles: next });
    }
    setFetchingFootprints(true);
    try {
      const results = await listFootprintsInPolygon.call({ polygon });
      setFootprints(results || []);
      toast.success(strings.FootprintsFound((results || []).length));
    }
    catch (error: any) {
      toast.error(error.message || `${error}`);
    }
    finally {
      setFetchingFootprints(false);
    }
  };
  const [footprintEnrichment, setFootprintEnrichment] = useState<FootprintEnrichment>();
  const [selectedFootprint, setSelectedFootprint] = useState<BuildingFootprint>();
  const footprintRequestRef = useRef(0);
  const handleFootprintClick = async (f: BuildingFootprint) => {
    if (!f.centroid) return;
    const token = ++footprintRequestRef.current;
    setSelectedFootprint(f);
    setFootprintEnrichment(undefined);
    try {
      const enrichment = await geocodeFootprint.call({ centroid: f.centroid });
      if (token !== footprintRequestRef.current) return;
      if (!enrichment) {
        toast.error(strings.UnableToGeocodeFootprint());
        closeFootprintModal();
        return;
      }
      setFootprintEnrichment(enrichment);
    }
    catch (error: any) {
      if (token !== footprintRequestRef.current) return;
      toast.error(error.message || `${error}`);
      closeFootprintModal();
    }
  };
  const [footprintModalState, setFootprintModalState] = useState<FootprintModalState>('idle');
  const [footprintFullDetails, setFootprintFullDetails] = useState<{ label: string, value: any }[]>();
  const [enrichedProspectId, setEnrichedProspectId] = useState<string>();
  const closeFootprintModal = () => {
    footprintRequestRef.current++;
    setFootprintEnrichment(undefined);
    setSelectedFootprint(undefined);
    setFootprintModalState('idle');
    setFootprintFullDetails(undefined);
    setEnrichedProspectId(undefined);
  };
  const handleAddFootprintToBatch = toastOnError(async () => {
    if (!_id || !footprintEnrichment) return;
    await addProspectToBatch.call({ batchId: _id, addressString: footprintEnrichment.addressString });
    toast.success(strings.Added());
    setAddressKey((k) => k + 1);
    closeFootprintModal();
  });
  const handleEnrichFootprint = toastOnError(async () => {
    if (!_id || !footprintEnrichment) return;
    const prospectId = await addProspectToBatch.call({ batchId: _id, addressString: footprintEnrichment.addressString });
    if (!prospectId) { toast.error(strings.UnableToGeocodeFootprint()); return; }
    setEnrichedProspectId(prospectId);
    setFootprintModalState('enriching');
    setAddressKey((k) => k + 1);
    try {
      await enrichProspect.call({ prospectId, sync: true });
      const details = await getProspectFullDetails.call({ prospectId });
      setFootprintFullDetails(details);
      setFootprintModalState('enriched');
    }
    catch (error: any) {
      toast.error(error.message || `${error}`);
      setFootprintModalState('idle');
    }
  });
  const handleRemoveEnrichedFootprint = toastOnError(async () => {
    if (!enrichedProspectId) return;
    await prospectModelClass.deleteMethod.call(enrichedProspectId);
    await prospectModelClass.deleteMethod.call(enrichedProspectId);
    setAddressKey((k) => k + 1);
    closeFootprintModal();
  });
  const [addAllProgress, setAddAllProgress] = useState<{ done: number, total: number } | undefined>();
  const handleAddAll = toastOnError(async () => {
    if (!_id || !filteredFootprints.length || addAllProgress) return;
    const total = filteredFootprints.length;
    setAddAllProgress({ done: 0, total });
    let done = 0;
    try {
      for (const f of filteredFootprints) {
        if (!f.centroid) { done++; setAddAllProgress({ done, total }); continue; }
        try {
          const enrichment = await geocodeFootprint.call({ centroid: f.centroid });
          if (enrichment?.addressString) await addProspectToBatch.call({ batchId: _id, addressString: enrichment.addressString });
        }
        catch (_) {/**/ }
        done++;
        setAddAllProgress({ done, total });
      }
      toast.success(strings.Added());
      setAddressKey((k) => k + 1);
    }
    finally {
      setAddAllProgress(undefined);
    }
  });

  const selectedProspects = useMemo(
    () => prospects.filter((p) => selectedIds.includes(p._id)),
    [prospects, selectedIds],
  );

  const parcelLayers = useMemo<MapLayer[]>(() => {
    return selectedProspects
      .filter((p) => p.type === 'apn' && p.parcel?.boundary)
      .map((p) => ({
        id: `parcel-${p._id}`,
        kind: 'geojson' as const,
        visible: true,
        data: p.parcel!.boundary,
        style: { fillColor: 'orange', fillOpacity: 0.35, strokeColor: 'orange', strokeWeight: 2 },
      }));
  }, [selectedProspects]);

  const footprintLayers = useMemo<MapLayer[]>(() => {
    if (!filteredFootprints.length) return [];
    return [{
      id: 'footprints',
      kind: 'geojson' as const,
      visible: true,
      data: { type: 'FeatureCollection', features: filteredFootprints },
      style: { fillColor: '#00ff00', fillOpacity: 0.4, strokeColor: '#00ff00', strokeWeight: 1 },
    }];
  }, [filteredFootprints]);

  const [deletingProspect, setDeletingProspect] = useState<Prospect | undefined>();
  const removeProspect = toastOnError(async (prospect: Prospect) => {
    await prospectModelClass.deleteMethod.call(prospect._id); // soft delete
    await prospectModelClass.deleteMethod.call(prospect._id); // hard delete
    setDeletingProspect(undefined);
    setSelectedIds((ids) => ids.filter((id) => id !== prospect._id));
    setAddressKey((k) => k + 1);
  });

  const addAddress = toastOnError(async (address?: GmapsAddress) => {
    if (!_id || !address?.formatted) return;
    const prospectId = await addProspectToBatch.call({ batchId: _id, addressString: address.formatted, address });
    toast.success(strings.Added());
    setSelectedIds((ids) => [...ids, prospectId]);
    setAddressKey((k) => k + 1);
    if (address.location) {
      setMapCenter(address.location);
      setMapZoom((z) => Math.max(z || 0, 15));
    }
  });

  const [enrichingProspectId, setEnrichingProspectId] = useState<string | undefined>();
  const [enrichQueueLength, setEnrichQueueLength] = useState(0);
  const [enrichQueueTotal, setEnrichQueueTotal] = useState(0);
  const enriching = enrichQueueLength > 0;
  const handleEnrich = toastOnError(async () => {
    if (!_id || !selectedIds.length) return;
    // Chunked so a large selection doesn't exceed the DDP frame limit and drop the connection.
    const CHUNK = 1000;
    for (let i = 0; i < selectedIds.length; i += CHUNK) {
      await enqueueProspectEnrichments.call({ batchId: _id, prospectIds: selectedIds.slice(i, i + CHUNK) });
    }
    setEnrichQueueLength(selectedIds.length);
    setEnrichQueueTotal(selectedIds.length);
  });
  const cancelEnrichments = toastOnError(async () => {
    if (!_id) return;
    await cancelProspectEnrichments.call({ batchId: _id });
    setEnrichQueueLength(0);
    setEnrichQueueTotal(0);
  });
  const handleEnrichIncomplete = toastOnError(async () => {
    if (!_id) return;
    const queued = await enqueueIncompleteEnrichments.call({ batchId: _id });
    if (!queued) return toast.success(strings.NoIncompleteProspects());
    toast.success(strings.QueuedForEnrichment(queued));
    setEnrichQueueLength((n) => n + queued);
    setEnrichQueueTotal((n) => n + queued);
  });
  useEffect(() => {
    if (!_id) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const progress = await getProspectEnrichProgress.call({ batchId: _id });
        if (cancelled) return;
        setEnrichingProspectId(progress?.enrichingProspectId);
        setEnrichQueueLength(progress?.queueLength || 0);
        setEnrichQueueTotal(progress?.total || 0);
      }
      catch (_) {/**/ }
    };
    poll();
    const interval = setInterval(poll, 2000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [_id]);
  const enrichingProspect = useMemo(
    () => prospects.find((p) => p._id === enrichingProspectId),
    [prospects, enrichingProspectId],
  );
  const enrichProgressTip = useMemo(() => {
    if (!enriching) return undefined;
    const completed = Math.max(0, enrichQueueTotal - enrichQueueLength);
    return strings.EnrichingProgress(completed, enrichQueueTotal);
  }, [enriching, enrichQueueTotal, enrichQueueLength, enrichingProspect]);
  const handleExport = toastOnError(async () => {
    const url = await exportProspectsToXlsx.call({ prospectIds: selectedIds });
    if (url) clientDownloadUrl(url);
  });
  const [fileProgress, setFileProgress] = useState<string | undefined>();
  const handleFile = async (file: File) => {
    if (!_id) return file;
    const text = await file.text();
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
    let added = 0;
    for (const line of lines) {
      setFileProgress(`${added + 1} / ${lines.length} : ${line}`);
      await addProspectToBatch.call({ batchId: _id, addressString: line });
      added += 1;
    }
    setFileProgress(undefined);
    toast.success(`${strings.Added()} ${added}`);
    setAddressKey((k) => k + 1);
    return file;
  };

  if (!isLoaded) return <LoadingFlex />;
  return <>
    <ModelFormPage
      modelClass={modelClass}
      styled={ProspectBatchPageStyled}
      permission={() => canProspect}
      documentRef={documentRef}
      defaultUpdate={defaultUpdate}
      update={update}
      setUpdate={setUpdate}
      formDeps={[fileProgress]}
      formSpec={() => [
        [
          { field: 'name' },
          {
            field: 'addAddress', input: () => (
              <FormField key={`addAddress-${addressKey}`} field="addAddress">
                <label>{strings.Address()}</label>
                <AddressInput onChange={(address) => addAddress(address)} autoCompleteRequest={{ locationBias: sanDiegoCoords }} inline />
              </FormField>
            )
          },
          {
            field: 'uploadFile', input: () => (
              <FormField key="uploadFile" field="uploadFile" className="upload-file">
                <label>{strings.UploadFile()}</label>
                {fileProgress
                  ? <div className="file-progress">{fileProgress}</div>
                  : <FileInput accept=".txt,.csv,text/plain,text/csv" maxSize={maxTextImportBytes} onFile={handleFile} />
                }
              </FormField>
            )
          },
          {
            field: 'useNearmap', input: () => (
              <FormField key="useNearmap" field="useNearmap" className="enrichment-flag">
                <label>
                  <Checkbox checked={update.useNearmap ?? documentRef.current?.useNearmap ?? false} onChange={(checked) => {
                    setUpdate((u) => ({ ...u, useNearmap: checked }));
                    _id && modelClass.updateMethod.call({ _id, useNearmap: checked });
                  }} />
                  <span>{strings.UseNearmap()}</span>
                </label>
              </FormField>
            )
          },
          {
            field: 'useIca', input: () => (
              <FormField key="useIca" field="useIca" className="enrichment-flag">
                <label>
                  <Checkbox checked={update.useIca ?? documentRef.current?.useIca ?? false} onChange={(checked) => {
                    setUpdate((u) => ({ ...u, useIca: checked }));
                    _id && modelClass.updateMethod.call({ _id, useIca: checked });
                  }} />
                  <span>{strings.UseIca()}</span>
                </label>
              </FormField>
            )
          },
        ],
      ]}
      pageFooter={
        <div className="footer-wrap">
          <div className="prospects-panel">
            <h2>
              {prospectModelClass.pluralName}
              {enrichProgressTip
                ? <> ({enrichProgressTip}) <span className="cancel-enrich" onClick={() => cancelEnrichments()}>{strings.Cancel()}</span></>
                : <span className="enrich-incomplete" onClick={() => handleEnrichIncomplete()}>{strings.EnrichIncomplete()}</span>}
            </h2>
            <ListMethodCatalog
              key={addressKey}
              listMethod={prospectModelClass.listMethod}
              query={prospectQuery}
              columns={[
                { field: 'name', label: strings.Address() },
                { field: 'type', render: (p: Prospect) => <span>{p.type === 'apn' ? 'APN' : strings.Address()}</span> },
                { field: 'remove', label: ' ', render: (p: Prospect) => <Icon icon="cancel" onClick={() => setDeletingProspect(p)} tip={strings.Remove()} /> },
              ]}
              defaultSort={{ createdAt: -1 }}
              defaultSelectedIds={selectedIds}
              onSelectedIds={setSelectedIds}
              onDataLoaded={setProspects}
              rowClass={(p: Prospect) => p._id === enrichingProspectId ? 'enriching' : ''}
              tools={selectedIds.length > 0 && <div className="prospect-catalog-buttons">
                <Button
                  disabled={enriching}
                  onClick={() => handleEnrich()}
                  tip={enrichProgressTip}
                >{enriching ? <Loading /> : <>{strings.Enrich()} ({selectedIds.length})</>}</Button>
                <Button onClick={() => handleExport()}>{strings.Export()} ({selectedIds.length})</Button>
              </div>}
            />
          </div>
          <div className="map-wrap">
            {toggles}
            <div className="drawing-tools">
              <Button onClick={() => setDrawingMode('rectangle')} className={drawingMode === 'rectangle' ? 'active' : ''}>{strings.Rectangle()}</Button>
              <Button onClick={() => setDrawingMode('circle')} className={drawingMode === 'circle' ? 'active' : ''}>{strings.Circle()}</Button>
              <span className="drawing-hint">{strings.DrawingHint()}</span>
            </div>
            <div className="prospector-map-wrap">
              {(fetchingFootprints || layersLoading) && (
                <div className="map-loading">
                  <Loading />
                  <span>{strings.Loading()}…</span>
                </div>
              )}
              <ProspectorMap layers={[...layers, ...parcelLayers, ...footprintLayers]} height="40rem" center={mapCenter} zoom={mapZoom} onMapLoad={setMapInstance}>
                {selectedProspects
                  .filter((p) => {
                    if (p.type === 'apn') return !!p.parcel?.centroid;
                    return !!p.address?.location;
                  })
                  .map((p) => (
                    <Marker
                      key={p._id}
                      position={p.type === 'apn' ? p.parcel!.centroid! : p.address!.location}
                      title={p.name}
                    />
                  ))
                }
                {/* react-google-maps Rectangle/Circle silently never draw unless attached to a loaded map. */}
                {mapInstance && rectangles.map((r, i) => (
                  <Rectangle key={`rect-${i}`} bounds={r} options={savedRegionOptions} />
                ))}
                {mapInstance && circles.map((c, i) => (
                  <Circle key={`circle-${i}`} center={c.center} radius={c.radius} options={savedRegionOptions} />
                ))}
                {mapInstance && <DrawingTool map={mapInstance} mode={drawingMode} onComplete={handleDrawingComplete} />}
                {mapInstance && <MeasurementTool map={mapInstance} measurements={measurements} onChange={handleMeasurementsChange} />}
              </ProspectorMap>
              {
                footprints.length ?
                  <div className="footprint-list">
                    <div className="footprint-list-header">
                      <Button onClick={() => setShowFiltersModal(true)}>{strings.Filters()}</Button>
                      <Button disabled={!!addAllProgress} onClick={() => handleAddAll()}
                        tip={addAllProgress ? strings.AddingProgress(addAllProgress.done, addAllProgress.total) : undefined}
                      >{addAllProgress ? <Loading /> : strings.AddAll()}</Button>
                    </div>
                    {
                      filteredFootprints.map((f) => (
                        <div
                          key={f.id}
                          className={`footprint-row${batchedFootprintIds.has(f.id ?? f._id) ? ' in-batch' : ''}`}
                          onClick={() => handleFootprintClick(f)}
                        >
                          <span>{Math.round(f.area || 0).toLocaleString()} sq.ft.</span>
                        </div>
                      ))
                    }
                  </div>
                  :
                  null
              }
            </div>
          </div>
        </div>
      }
    />
    {
      deletingProspect ?
        <ConfirmModal
          title={strings.Remove()}
          body={<div>{strings.AreYouSureYouWantToDeleteTheProspect()} <b>{deletingProspect.name}</b>?</div>}
          onClose={() => setDeletingProspect(undefined)}
          onConfirm={() => removeProspect(deletingProspect)}
        />
        :
        null
    }
    {
      showFiltersModal ?
        <FootprintFiltersModal
          filters={filters}
          onApply={setFilters}
          onClose={() => setShowFiltersModal(false)}
        />
        :
        null
    }
    {
      selectedFootprint ?
        <FootprintDetailModal
          state={footprintModalState}
          enrichment={footprintEnrichment}
          area={selectedFootprint.area}
          fullDetails={footprintFullDetails}
          onAdd={handleAddFootprintToBatch}
          onEnrich={handleEnrichFootprint}
          onRemove={handleRemoveEnrichedFootprint}
          onClose={closeFootprintModal}
        />
        :
        null
    }
  </>;
};
